import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Flex,
  HStack,
  Text,
  Button,
  VStack,
  IconButton,
  Heading,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Container,
} from "@chakra-ui/react";
import { BiChevronLeft, BiEditAlt, BiSend } from "react-icons/bi";
import NokDisplay from "../components/RequestDocument/NokDisplay";
import Documents from "../components/RequestDocument/Documents";
import FinanceApproval from "../components/RequestDocument/FinanceApproval";
import ApprovalWorkFlow from "../components/RequestDocument/ApprovalWorkFlow";
import {
  commentLogs,
  getALoanRequest,
  getLoanRequestSLA,
  sendDirectDebitMandate,
  getMonoEligibleBanks,
  validateBankAccount,
  FinanceProceed,
} from "../services/axios";
import GuarantorInformation from "../components/RequestDocument/GuarantorInformation";
import { Link, useParams, useNavigate } from "react-router-dom";
import ApproveLoanModal from "../components/Modals/ApproveLoanModal";
import RejectionModal from "../components/Modals/RejectionModal";
import BorrowersInformation from "../components/RequestDocument/BorrowersInformation";
import CreditApprovalModal from "../components/Modals/CreditApprovalModal";
import FinanceApproveLoanModal from "../components/Modals/FinanceApproveLoanModal";
import VerifyLoanModal from "../components/Modals/VerifyLoanModal";
import BorrowerSignature from "../components/RequestDocument/BorrowerSignature";
import LogLoanModal from "../components/RequestDocument/LogLoanModal";
import Loader from "../components/Loader/loader";
import RequestSLA from "../components/RequestSLA";
import RepaymentSchedule from "../components/RequestDocument/RepaymentSchedule";
import TransactionLog from "../components/RequestDocument/TransactionLog";
import InstantLoanOfferDetails from "../components/RequestDocument/LoanOfferDetails";
import { toast } from "react-toastify";
import ModalLayout from "../components/Layout/ModalLayout";
import RepaymentConfig from "../components/RequestDocument/RepaymentConfig";
import UpdateLoanInfoModal from "../components/Modals/UpdateLoanInfoModal";
import MandateForm from "../components/RequestDocument/MandateForm";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const RequestPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const isRejectedLoans = sessionStorage.getItem("rejected");
  const [formValues, setFormValues] = useState({
    monthly_repayment: "",
    bank_code: "",
    account_number: "",
    gateway: "",
  });

  useEffect(() => {
    if (formValues?.account_number?.length === 10 && formValues?.bank_code) {
      const validateAccount = async () => {
        setVerifyingAccount(true);
        try {
          const { data } = await validateBankAccount(
            formValues.account_number,
            formValues.bank_code
          );
          setVerifyingAccount(false);
          setAccountInfo(data?.account_name);
        } catch (error) {
          setVerifyingAccount(false);
          toast.error("We are unable to verify the account.");
        }
      };
      validateAccount();
    }
  }, [formValues.account_number, formValues.bank_code]);

  const role = localStorage.getItem("role");

  const [loanRequest, setLoanRequest] = useState(null);
  const [accountInfo, setAccountInfo] = useState("");
  const [loanLogs, setLoanLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(10);
  const [processing, setProcessing] = useState(false);
  const [loanRequestSLA, setLoanRequestSLA] = useState(null);
  const [processingMandate, setProcessingMandate] = useState(false);
  const [verifyingAccount, setVerifyingAccount] = useState(false);
  const [openUpdateModal, setOpenUpdateModal] = useState(false);
  const [availableBanks, setAvailableBanks] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [processingFinanceProceed, setProcessingFinanceProceed] = useState(false);

  const getLogs = useCallback(async (theLimit) => {
    setProcessing(true);
    const { data } = await commentLogs(id, theLimit);
    if (data) setLoanLogs(data?.data);
    setProcessing(false);
  }, [id]);


  const sendMandateHandler = async () => {
    if (!formValues.monthly_repayment) return toast.error("Please enter repayment amount");
    if (!formValues.gateway) return toast.error("Please select a gateway");

    const payload = {
      monthly_repayment: Number(formValues.monthly_repayment),
      requestId: id,
      gateway: formValues.gateway,
      account_number: formValues.account_number,
      bank_code: formValues.bank_code,
    };

    setProcessingMandate(true);
    const res = await sendDirectDebitMandate(payload);
    setProcessingMandate(false);
    if (res) {
      toast.success("Mandate sent successfully");
      setIsOpen(false);
      setFormValues({ monthly_repayment: "", bank_code: "", account_number: "", gateway: "" });
    }
  };

  const financeProceedHandler = async () => {
    setProcessingFinanceProceed(true);
    const data = await FinanceProceed(id);
    setProcessingFinanceProceed(false);
    if (data) {
      toast.success(data?.message || "Loan offer sent to customer");
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const loanRes = await getALoanRequest(id);
        if (loanRes?.data) {
          setLoanRequest(loanRes.data);
          
          // Fetch secondary data without blocking
          getMonoEligibleBanks().then((res) => {
            if (res?.data) setAvailableBanks(res.data);
          });
          getLoanRequestSLA(id).then((res) => {
            if (res?.data) setLoanRequestSLA(res.data);
          });
          getLogs(limit);
        }
      } catch (err) {
        toast.error("An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, getLogs, limit]);

  
  if (loading) {
    return (
      <Flex justify="center" align="center" h="80vh">
        <Loader />
      </Flex>
    );
  }

  if (!loanRequest) {
    return (
      <Flex justify="center" align="center" h="80vh">
        <Text fontSize="lg" color="gray.500">Loan request not found.</Text>
      </Flex>
    );
  }

  return (
    <Box bg="gray.50" minH="100vh">
      <UpdateLoanInfoModal
        loanRequest={loanRequest}
        isOpen={openUpdateModal}
        setIsOpen={setOpenUpdateModal}
      />

      <Box bg="white" borderBottom="1px solid" borderColor="gray.100" py={4} position="sticky" top={0} zIndex={10}>
        <Container maxW="container.xl">
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
            <VStack align="flex-start" spacing={1}>
              <HStack spacing={2} color="gray.500" fontSize="xs">
                <IconButton 
                  icon={<BiChevronLeft size={20} />} 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate(-1)}
                  aria-label="Back"
                />
                <Breadcrumb separator={<Text color="gray.300">/</Text>}>
                  <BreadcrumbItem>
                    <BreadcrumbLink as={Link} to="/dashboard">Dashboard</BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbItem isCurrentPage>
                    <BreadcrumbLink fontWeight="bold" color="brand.500">Loan Request</BreadcrumbLink>
                  </BreadcrumbItem>
                </Breadcrumb>
              </HStack>
              <Heading size="md" color="brand.900">
                {loanRequest?.user?.first_name} {loanRequest?.user?.last_name}
              </Heading>
            </VStack>

            <HStack spacing={4}>
              <RequestSLA loanRequestSLA={loanRequestSLA} />
              
              <HStack spacing={2}>
                {!isRejectedLoans && (
                  <>
                    {role === "Verification" ? (
                      <VerifyLoanModal id={id} />
                    ) : (
                      <>
                        <RejectionModal id={id} />
                        {role === "Finance Ops" ? (
                          <CreditApprovalModal id={id} />
                        ) : role === "Finance Admin" ? (
                          loanRequest?.finance_approval === "approved" ? (
                            loanRequest?.verification_approval !== "pending" &&
                            loanRequest?.finance_final_decision === "pending" && (
                              <Button
                                leftIcon={<BiSend />}
                                colorScheme="brand"
                                variant="solid"
                                size="md"
                                isLoading={processingFinanceProceed}
                                onClick={financeProceedHandler}
                              >
                                PROCEED TO OFFER
                              </Button>
                            )
                          ) : (
                            <FinanceApproveLoanModal id={id} />
                          )
                        ) : (
                          <ApproveLoanModal id={id} />
                        )}
                      </>
                    )}
                  </>
                )}

                {
                 loanRequest?.finance_final_decision === "approved" &&
                 !loanRequest?.direct_debit_mandate_active && (
                  <Button
                    leftIcon={<BiSend />}
                    colorScheme="brand"
                    variant="solid"
                    size="md"
                    onClick={() => setIsOpen(true)}
                  >
                    SEND MANDATE
                  </Button>
                )}

                <Button
                  leftIcon={<BiEditAlt />}
                  variant="outline"
                  borderColor="brand.500"
                  color="brand.500"
                  size="md"
                  _hover={{ bg: "brand.50", color: "brand.600" }}
                  onClick={() => setOpenUpdateModal(true)}
                >
                  UPDATE INFO
                </Button>
              </HStack>
            </HStack>
          </Flex>
        </Container>
      </Box>

      <Container maxW="container.xl" py={8}>
        <VStack spacing={8} align="stretch">
          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <BorrowersInformation loanRequest={loanRequest} />
          </MotionBox>
          
          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <NokDisplay loanRequest={loanRequest} />
          </MotionBox>

          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <GuarantorInformation id={id} />
          </MotionBox>

          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Documents loanRequest={loanRequest} />
          </MotionBox>

          <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
            <BorrowerSignature loanRequest={loanRequest} />
          </MotionBox>

          {loanRequest?.loan && (
            <MotionBox initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <FinanceApproval loanInfo={loanRequest?.loan} />
            </MotionBox>
          )}

          <ApprovalWorkFlow loanRequest={loanRequest} />
          
          <LogLoanModal
            processing={processing}
            getLogs={getLogs}
            loanLogs={loanLogs}
            limit={limit}
            setLimit={setLimit}
          />

          {loanRequest?.config?.authorization_code && (
            <RepaymentConfig config={loanRequest?.config} />
          )}

          {loanRequest?.loan && (
            <InstantLoanOfferDetails loanRequest={loanRequest} />
          )}

          {loanRequest?.repaymentSchedule?.length > 0 && (
            <RepaymentSchedule loanRequest={loanRequest} />
          )}

          {loanRequest?.transactions?.length > 0 && (
            <TransactionLog loanRequest={loanRequest} />
          )}
        </VStack>
      </Container>

      <ModalLayout
        cancelBtnText="BACK"
        title="Send Direct Debit Mandate"
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        loading={processingMandate}
        backHandler={() => setIsOpen(false)}
        rejectHandler={sendMandateHandler}
      >
        <MandateForm
          formValues={formValues}
          setFormValues={setFormValues}
          availableBanks={availableBanks}
          verifyingAccount={verifyingAccount}
          accountInfo={accountInfo}
        />
      </ModalLayout>
    </Box>
  );
};

export default RequestPage;
